import { useMemo } from 'react';
import { useDetailedSimulation } from '../store/detailedSimulation';
import { useRegulatorAPI } from './useRegulatorAPI';
import { useColomaMqtt } from '../store/coloma-mqtt';
import { useMockMode } from '../store/mockMode';
import { toCanonicalId } from '../utils/canonicalId';
import type { IntersectionDefinition, ColorCode } from '../types/intersection';
import { ColorCode as CC } from '../types/intersection';

interface IntersectionState {
  groupColors: ColorCode[];
  detectorStates: boolean[];
  currentPhase: string;
  cyclePosition: number;
  cycleTime: number;
  isRunning: boolean;
  speed: number;
  plan: number;     // plan activo del regulador
  estado: string;   // estado operativo del regulador (string del payload)
  online: boolean;
}

// Mapeo p-code UNE → ColorCode. Compartido por los modos 'api' y 'mqtt'.
const pcodeToColor: Record<number, ColorCode> = {
  0: CC.D, 1: CC.V, 2: CC.A, 3: CC.R, 4: CC.N,
  5: CC.S, 6: CC.J, 7: CC.I, 8: CC.F, 9: CC.C,
  10: CC.P, 11: CC.G, 14: CC.B, 15: CC.H, 16: CC.K,
  22: CC.E, 39: CC.Z,
};

/**
 * Hook puente entre una fuente de datos y el renderizado de una intersección.
 * Devuelve los colores de grupo y el estado calculados para la fase actual.
 *
 * @param intersection - definición de la intersección
 * @param dataSource - 'simulation' (local), 'api' (WS FastAPI) o 'mqtt' (broker ARS93)
 */
export function useIntersectionState(
  intersection: IntersectionDefinition,
  dataSource: 'simulation' | 'api' | 'mqtt' = 'simulation',
): IntersectionState {
  // Read from all sources (hooks must run unconditionally)
  const sim = useDetailedSimulation();
  const api = useRegulatorAPI();
  const mqttStatus = useColomaMqtt(
    s => s.reguladores[toCanonicalId(intersection.cabinet.logicalId)],
  );
  const mockEnabled = useMockMode(s => s.enabled);

  const source = dataSource === 'api' ? api : sim;
  const { cyclePosition: rawCyclePosition, cycleTime, isRunning, speed } = source;
  const phaseDurations: number[] = sim.phaseDurations;

  // Apply phase offset: shift which phase this intersection displays
  const PHASES = ['F1', 'F2', 'F3', 'F4'];
  const offset = intersection.phaseOffset ?? 0;
  const { effectivePhase, effectiveCyclePosition } = useMemo(() => {
    if (offset === 0) {
      return { effectivePhase: source.currentPhase, effectiveCyclePosition: rawCyclePosition };
    }
    let offsetDs = 0;
    for (let i = 0; i < offset; i++) {
      offsetDs += phaseDurations[i];
    }
    const shifted = (rawCyclePosition + offsetDs) % cycleTime;
    let acc = 0;
    let phase = 'F1';
    for (let i = 0; i < 4; i++) {
      acc += phaseDurations[i];
      if (shifted < acc) {
        phase = PHASES[i];
        break;
      }
    }
    return { effectivePhase: phase, effectiveCyclePosition: shifted };
  }, [source.currentPhase, rawCyclePosition, cycleTime, offset, phaseDurations]);

  const currentPhase = effectivePhase;
  const cyclePosition = effectiveCyclePosition;

  const apiDrivers = useRegulatorAPI(s => s.drivers);
  const apiActiveId = useRegulatorAPI(s => s.activeDriverId);

  // Compute group colors (simulation / api)
  const groupColors = useMemo(() => {
    if (dataSource === 'api' && apiActiveId !== null) {
      const driver = apiDrivers[String(apiActiveId)];
      if (driver && driver.groups && driver.groups.length > 0) {
        return driver.groups.map(g => pcodeToColor[g.pcode] ?? CC.D);
      }
    }
    return intersection.groups.map(group => {
      const color = group.phaseColors[currentPhase];
      return color ?? CC.D;
    });
  }, [intersection.groups, currentPhase, dataSource, apiDrivers, apiActiveId]);

  // Compute detector states (simulation / api)
  const detectorStates = useMemo(() => {
    if (dataSource === 'api' && apiActiveId !== null) {
      const driver = apiDrivers[String(apiActiveId)];
      if (driver && driver.detectors && driver.detectors.length > 0) {
        return intersection.detectors.map((_, i) =>
          i < driver.detectors.length ? driver.detectors[i] : false
        );
      }
    }
    return intersection.detectors.map(detector => {
      if (!isRunning) return false;
      if (detector.type === 'loop') {
        if (detector.id <= 2) return currentPhase === 'F1' || currentPhase === 'F2';
        return currentPhase === 'F3' || currentPhase === 'F4';
      }
      return false;
    });
  }, [intersection.detectors, currentPhase, isRunning, dataSource, apiDrivers, apiActiveId]);

  // --- Modo MQTT: estado real desde el broker de ARS93 ---
  const mqttResult = useMemo<IntersectionState>(() => {
    const online = mqttStatus?.online ?? false;
    const estado = online && mqttStatus ? mqttStatus.estado : 'desconectado';
    const colors = intersection.groups.map((group) => {
      if (!online || !mqttStatus) return CC.D;
      const g = mqttStatus.grupos.find(gr => gr.grupo === group.id);
      return g ? (pcodeToColor[g.estado_une] ?? CC.D) : CC.D;
    });
    const fase = mqttStatus?.fase;
    return {
      groupColors: colors,
      detectorStates: intersection.detectors.map(() => false),
      currentPhase: fase != null && fase !== '' ? String(fase) : '—',
      cyclePosition: 0,
      cycleTime: 0,
      isRunning: online,
      speed: 1,
      plan: mqttStatus?.plan ?? 0,
      estado,
      online,
    };
  }, [mqttStatus, intersection.groups, intersection.detectors]);

  if (dataSource === 'mqtt') {
    // Regulador con datos MQTT reales → siempre su estado real.
    if (mqttStatus) return mqttResult;
    // Sin datos reales: en modo "red simulada" se muestra operativo (demo);
    // si no, desconectado.
    if (mockEnabled) {
      return {
        groupColors,
        detectorStates,
        currentPhase,
        cyclePosition,
        cycleTime,
        isRunning: true,
        speed,
        plan: source.currentPlan,
        estado: 'activo',
        online: true,
      };
    }
    return mqttResult;
  }

  return {
    groupColors,
    detectorStates,
    currentPhase,
    cyclePosition,
    cycleTime,
    isRunning,
    speed,
    plan: source.currentPlan,
    estado: isRunning ? 'activo' : 'desconectado',
    online: isRunning,
  };
}
